import React, { useState, useEffect } from "react";
import { Select, Spin, Alert } from "antd";
import { useLazyGetEntityDataQuery } from "../../Redux/service/Forms/FormEntities";

/**
 * DynamicDropdownField - Renders a dynamic dropdown that fetches data from entities.
 *
 * Props:
 * - field: The form field configuration (contains dropdownOptions with entityConfig)
 * - value: Current value
 * - onChange: Callback when value changes
 * - disabled: Whether the field is disabled
 * - placeholder: Placeholder text
 * - parentValue: Value from parent dropdown (for dependencies)
 * - submitData: Current form submission data (to read parent values)
 */
const DynamicDropdownField = ({
	field,
	value,
	onChange,
	disabled = false,
	placeholder,
	parentValue = null,
	submitData = [],
	scopeValues = null,
	includeProperties = [],
	multiple = false,
}) => {
	const [options, setOptions] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [page, setPage] = useState(0);
	const limit = 50;

	const [fetchEntityData, { data: entityData, isLoading, error }] =
		useLazyGetEntityDataQuery();

	// Extract entity config from field
	const entityConfig = field?.dropdownOptions?.entityConfig;
	const entityKey = entityConfig?.entityKey;
	const displayField = entityConfig?.displayField;
	const parentEntityKey = entityConfig?.dependsOn?.parentEntityKey;
	const includePropertiesKey = includeProperties.join(",");
	const isDynamic =
		field?.dropdownOptions?.queryKey === "dynamic" ||
		!!field?.dropdownOptions?.entityConfig?.entityKey;

	// Determine the actual parent value for dependency filtering
	const getParentValue = () => {
		// If parentValue prop is passed directly, use it
		if (parentValue) return parentValue;

		// If this field has a dependency config, try to read from submitData
		if (entityConfig?.dependsOn?.parentFieldKey && scopeValues) {
			const value = scopeValues[entityConfig.dependsOn.parentFieldKey];
			if (Array.isArray(value)) return value[0]?.id ?? value[0] ?? null;
			return value?.id ?? value ?? null;
		}

		if (entityConfig?.dependsOn?.parentFieldKey && submitData?.length > 0) {
			const parentField = submitData.find(
				(item) => item.key === entityConfig.dependsOn.parentFieldKey
			);
			if (parentField) {
				// Handle enriched value format (single or multi)
				const val = parentField.value;
				if (Array.isArray(val) && val.length > 0) {
					const first = val[0];
					return first && typeof first === "object" && first.id ? first.id : first;
				}
				if (val && typeof val === "object" && val.id) {
					return val.id;
				}
				return val;
			}
		}

		return null;
	};

	const resolvedParentValue = getParentValue();

	// Fetch data when component mounts or dependencies change
	useEffect(() => {
		if (!isDynamic || !entityKey || !displayField) return;

		// If this field depends on a parent and parent has no value yet, don't fetch
		if (parentEntityKey && !resolvedParentValue) {
			setOptions([]);
			return;
		}

		const fetchParams = {
			entityKey,
			displayField,
			limit,
			offset: page * limit,
			includeProperties: includePropertiesKey
				? includePropertiesKey.split(",")
				: [],
		};

		if (searchTerm) {
			fetchParams.search = searchTerm;
		}

		// Add parent filter if dependency is configured
		if (parentEntityKey && resolvedParentValue) {
			fetchParams.parentEntityKey = parentEntityKey;
			fetchParams.parentValue = resolvedParentValue;
		}

		fetchEntityData(fetchParams);
	}, [isDynamic, entityKey, displayField, parentEntityKey, page, searchTerm, resolvedParentValue, includePropertiesKey, fetchEntityData]);

	// Update options when data is fetched
	useEffect(() => {
		if (entityData?.data) {
			if (page === 0) {
				setOptions(entityData.data);
			} else {
				setOptions((prev) => [...prev, ...entityData.data]);
			}
		}
	}, [entityData, page]);

	// Reset when parent value changes
	useEffect(() => {
		if (entityConfig?.dependsOn?.parentEntityKey) {
			setPage(0);
			setSearchTerm("");
			setOptions([]);
			// Clear current value since parent changed
			if (value && onChange) {
				onChange(multiple ? [] : undefined);
			}
		}
	}, [resolvedParentValue, multiple]);

	// Handle dynamic dropdown
	if (!isDynamic) {
		return null; // Non-dynamic dropdowns are handled by the regular Select
	}

	if (!entityConfig?.entityKey || !entityConfig?.displayField) {
		return (
			<Alert
				message="Configuration Error"
				description="Dynamic dropdown is not properly configured"
				type="error"
				showIcon
			/>
		);
	}

	// If depends on parent but no parent selected yet
	if (entityConfig?.dependsOn?.parentEntityKey && !resolvedParentValue) {
		return (
			<Select
				style={{ width: "100%" }}
				placeholder={`${placeholder || "Select"} (select parent first)`}
				disabled
				options={[]}
			/>
		);
	}

	const handleChange = (selectedId) => {
		const selectedOption = options.find(
			(opt) => opt.id === selectedId || opt.id?.toString() === selectedId?.toString()
		);
		if (selectedOption) {
			// Store enriched value
			onChange({
				...selectedOption,
				entityKey: entityConfig.entityKey,
				timestamp: new Date().toISOString(),
			});
		} else {
			onChange(selectedId);
		}
	};

	const handleSearch = (searchValue) => {
		setSearchTerm(searchValue);
		setPage(0);
	};

	const handlePopupScroll = (e) => {
		const { target } = e;
		if (
			target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
			entityData?.hasMore &&
			!isLoading
		) {
			setPage((prev) => prev + 1);
		}
	};

	// Get display value - handle both enriched and simple values
	const displayValue = multiple
		? Array.isArray(value)
			? value.map((v) => (v && typeof v === "object" && v.id !== undefined ? v.id : v))
			: []
		: value?.id !== undefined
		? value.id
		: value;

	const handleChangeMulti = (selectedIds) => {
		if (!Array.isArray(selectedIds)) {
			selectedIds = selectedIds ? [selectedIds] : [];
		}
		const selectedOptions = selectedIds
			.map((id) => options.find((opt) => String(opt.id) === String(id)))
			.filter(Boolean);
		if (selectedOptions.length > 0) {
			onChange(
					selectedOptions.map((opt) => ({
						...opt,
						entityKey: entityConfig.entityKey,
					timestamp: new Date().toISOString(),
				}))
			);
		} else {
			onChange([]);
		}
	};

	return (
		<Select
			mode={multiple ? "multiple" : undefined}
			style={{ width: "100%" }}
			placeholder={placeholder || field?.placeholder || "Select an option"}
			value={displayValue}
			onChange={multiple ? handleChangeMulti : handleChange}
			disabled={disabled}
			showSearch
			filterOption={false}
			onSearch={handleSearch}
			onPopupScroll={handlePopupScroll}
			loading={isLoading}
			allowClear
			notFoundContent={
				isLoading ? (
					<div style={{ textAlign: "center", padding: 20 }}>
						<Spin size="small" />
					</div>
				) : error ? (
					<Alert
						message="Error loading data"
						description={error?.data?.message || "Failed to fetch options"}
						type="error"
						showIcon
						style={{ margin: 8 }}
					/>
				) : (
					<div style={{ textAlign: "center", padding: 20, color: "#999" }}>
						No data available
					</div>
				)
			}
			options={options.map((opt) => ({
				label: opt.displayValue,
				value: opt.id,
			}))}
		/>
	);
};

export default DynamicDropdownField;
